import * as AgentSessionModule from "./session-state.js";
import * as AgentPlanningModule from "./planning.js";
import * as AgentObserveModule from "./observe.js";
import * as AgentQuestionsModule from "./questions.js";
import * as AgentReviseModule from "./revise.js";
import * as AgentRuntimeModule from "./runtime.js";
import * as AgentDraftModule from "./draft.js";
import * as AgentEditorApplyModule from "./editor-apply.js";
import * as AgentFlowExtractModule from "./flow-extract.js";
import * as AgentFlowPatchModule from "./flow-patch.js";

export function bindAgentModules(target, dependencies = {}) {
    if (!target) return;

    target.__agentModules = {
        session: AgentSessionModule,
        planning: AgentPlanningModule,
        observe: AgentObserveModule,
        questions: AgentQuestionsModule,
        revise: AgentReviseModule,
        runtime: AgentRuntimeModule,
        draft: AgentDraftModule,
        editorApply: AgentEditorApplyModule,
        flowExtract: AgentFlowExtractModule,
        flowPatch: AgentFlowPatchModule
    };

    if (typeof AgentSessionModule.createEmptyAgentSession === "function") {
        target.createEmptyAgentSession = AgentSessionModule.createEmptyAgentSession;
    }
    if (typeof AgentSessionModule.buildAgentPlanSteps === "function") {
        target.buildAgentPlanSteps = AgentSessionModule.buildAgentPlanSteps;
    }
    if (typeof AgentSessionModule.createPlanningAgentSession === "function") {
        target.createPlanningAgentSession = (userMessage = "") => AgentSessionModule.createPlanningAgentSession(userMessage, {
            createEmptyAgentSession: AgentSessionModule.createEmptyAgentSession,
            buildInitialAgentSessionData: AgentSessionModule.buildInitialAgentSessionData,
            buildAgentPlanSteps: AgentSessionModule.buildAgentPlanSteps,
            cloneJsonValue: dependencies.cloneJsonValue,
            buildLocalFlowDraft: dependencies.buildLocalFlowDraft,
            extractAxisNameFromText: dependencies.extractAxisNameFromText,
            extractTimeoutSecFromText: dependencies.extractTimeoutSecFromText,
            extractErrorCodeFromText: dependencies.extractErrorCodeFromText,
            detectOffBranchFromText: dependencies.detectOffBranchFromText,
            detectAfterMotionFromText: dependencies.detectAfterMotionFromText,
            applyPlanAnswerToDraft: dependencies.applyPlanAnswerToDraft
        });
    }
    if (typeof AgentSessionModule.createRestoredAgentSession === "function") {
        target.createRestoredAgentSession = (remote, persistedGoal = "") => AgentSessionModule.createRestoredAgentSession(remote, persistedGoal, {
            createEmptyAgentSession: AgentSessionModule.createEmptyAgentSession,
            buildAgentPlanSteps: AgentSessionModule.buildAgentPlanSteps,
            buildAssumptionsFromAnswers: AgentSessionModule.buildAssumptionsFromAnswers,
            cloneJsonValue: dependencies.cloneJsonValue,
            applyRemoteAgentSessionState: target.applyRemoteAgentSessionState
        });
    }
    if (typeof AgentSessionModule.applyAgentAnswerToSession === "function") {
        target.applyAgentAnswerToSession = (session, questionId, normalizedValue) => AgentSessionModule.applyAgentAnswerToSession(session, questionId, normalizedValue, {
            applyPlanAnswerToDraft: dependencies.applyPlanAnswerToDraft
        });
    }
    if (typeof AgentSessionModule.prepareAgentSessionForInitialDraft === "function") {
        target.prepareAgentSessionForInitialDraft = AgentSessionModule.prepareAgentSessionForInitialDraft;
    }
    if (typeof AgentSessionModule.buildAgentDraftRefreshConfig === "function") {
        target.buildAgentDraftRefreshConfig = AgentSessionModule.buildAgentDraftRefreshConfig;
    }
    if (typeof AgentSessionModule.buildAgentAnswerUiUpdate === "function") {
        target.buildAgentAnswerUiUpdate = AgentSessionModule.buildAgentAnswerUiUpdate;
    }
    if (typeof AgentSessionModule.buildAgentQuestionReasonText === "function") {
        target.buildAgentQuestionReasonText = AgentSessionModule.buildAgentQuestionReasonText;
    }
    if (typeof AgentSessionModule.buildAgentQuestionUi === "function") {
        target.buildAgentQuestionUi = (session, question) => AgentSessionModule.buildAgentQuestionUi(session, question, {
            buildPlanReasonText: AgentSessionModule.buildAgentQuestionReasonText
        });
    }
    if (typeof AgentSessionModule.buildAgentFinalizeUi === "function") {
        target.buildAgentFinalizeUi = AgentSessionModule.buildAgentFinalizeUi;
    }
    if (typeof AgentSessionModule.buildAgentLoopUi === "function") {
        target.buildAgentLoopUi = AgentSessionModule.buildAgentLoopUi;
    }
    if (typeof AgentSessionModule.applyRemoteAgentSessionState === "function") {
        target.applyRemoteAgentSessionState = (session, remote) => AgentSessionModule.applyRemoteAgentSessionState(session, remote, {
            cloneJsonValue: dependencies.cloneJsonValue,
            buildAssumptionsFromAnswers: AgentSessionModule.buildAssumptionsFromAnswers
        });
    }
    if (typeof AgentSessionModule.buildAgentRevisionInstruction === "function") {
        target.buildAgentRevisionInstruction = AgentSessionModule.buildAgentRevisionInstruction;
    }
    if (typeof AgentSessionModule.buildAssumptionsFromAnswers === "function") {
        target.buildAssumptionsFromAnswers = AgentSessionModule.buildAssumptionsFromAnswers;
    }
    if (typeof AgentSessionModule.buildFlowPlanOverview === "function") {
        target.buildFlowPlanOverview = AgentSessionModule.buildFlowPlanOverview;
    }
    if (typeof AgentSessionModule.clearPersistedAgentSessionReference === "function") {
        target.clearPersistedAgentSessionReference = AgentSessionModule.clearPersistedAgentSessionReference;
    }
    if (typeof AgentSessionModule.persistAgentSessionReference === "function") {
        target.persistAgentSessionReference = AgentSessionModule.persistAgentSessionReference;
    }

    if (typeof AgentObserveModule.createAgentObservation === "function") {
        target.createAgentObservation = AgentObserveModule.createAgentObservation;
    }
    if (typeof AgentObserveModule.buildAgentObservations === "function") {
        target.buildAgentObservations = (goalText, draft, answers = {}) => AgentObserveModule.buildAgentObservations(goalText, draft, answers, {
            findDraftConditionNode: dependencies.findDraftConditionNode,
            hasDraftConnection: dependencies.hasDraftConnection,
            findDraftMotionById: dependencies.findDraftMotionById,
            extractAxisNameFromText: dependencies.extractAxisNameFromText,
            detectOffBranchFromText: dependencies.detectOffBranchFromText,
            detectAfterMotionFromText: dependencies.detectAfterMotionFromText,
            extractTimeoutSecFromText: dependencies.extractTimeoutSecFromText,
            extractErrorCodeFromText: dependencies.extractErrorCodeFromText
        });
    }
    if (typeof AgentQuestionsModule.buildAgentQuestionsFromObservations === "function") {
        target.buildAgentQuestionsFromObservations = AgentQuestionsModule.buildAgentQuestionsFromObservations;
    }
    if (typeof AgentObserveModule.validateAgentDraft === "function") {
        target.validateAgentDraft = (draft) => AgentObserveModule.validateAgentDraft(draft, {
            findDraftMotionById: dependencies.findDraftMotionById
        });
    }
    if (typeof AgentQuestionsModule.decideNextAgentAction === "function") {
        target.decideNextAgentAction = AgentQuestionsModule.decideNextAgentAction;
    }

    if (typeof AgentRuntimeModule.advanceAgentSession === "function") {
        target.advanceAgentSession = AgentRuntimeModule.advanceAgentSession;
    }
    if (typeof AgentRuntimeModule.finalizeAgentSessionDraft === "function") {
        target.finalizeAgentSessionDraft = AgentRuntimeModule.finalizeAgentSessionDraft;
    }
    if (typeof AgentRuntimeModule.refreshAgentDraftFromSources === "function") {
        target.refreshAgentDraftFromSources = (session, instruction, options = {}) => AgentRuntimeModule.refreshAgentDraftFromSources(session, instruction, {
            requestRemoteAgentSessionCreate: dependencies.requestRemoteAgentSessionCreate,
            requestRemoteAgentSessionAnswer: dependencies.requestRemoteAgentSessionAnswer,
            requestAgentFlowDraft: dependencies.requestAgentFlowDraft,
            cloneJsonValue: dependencies.cloneJsonValue,
            applyAnswersToDraft: dependencies.applyAnswersToDraft,
            applyRemoteAgentSessionState: target.applyRemoteAgentSessionState,
            pushPlanActivity: dependencies.pushPlanActivity
        }, options);
    }
    if (typeof AgentRuntimeModule.deriveNextActionFromSessionState === "function") {
        target.deriveNextActionFromSessionState = AgentRuntimeModule.deriveNextActionFromSessionState;
    }
    if (typeof AgentRuntimeModule.shouldPreferRemoteAgentState === "function") {
        target.shouldPreferRemoteAgentState = AgentRuntimeModule.shouldPreferRemoteAgentState;
    }
    if (typeof AgentRuntimeModule.applyRemoteAgentSessionPollResult === "function") {
        target.applyRemoteAgentSessionPollResult = (session, remote, options = {}) => AgentRuntimeModule.applyRemoteAgentSessionPollResult(session, remote, {
            applyRemoteAgentSessionState: target.applyRemoteAgentSessionState,
            shouldAcceptRemoteAgentSessionState: AgentSessionModule.shouldAcceptRemoteAgentSessionState,
            syncAgentJobState: dependencies.syncAgentJobState
        }, options);
    }

    if (typeof AgentDraftModule.buildLocalFlowDraft === "function") {
        target.buildLocalFlowDraft = AgentDraftModule.buildLocalFlowDraft;
    }
    if (typeof AgentDraftModule.findDraftMotionById === "function") {
        target.findDraftMotionById = AgentDraftModule.findDraftMotionById;
    }
    if (typeof AgentDraftModule.hasDraftConnection === "function") {
        target.hasDraftConnection = AgentDraftModule.hasDraftConnection;
    }
    if (typeof AgentDraftModule.findDraftConditionNode === "function") {
        target.findDraftConditionNode = AgentDraftModule.findDraftConditionNode;
    }
    if (typeof AgentDraftModule.removeDraftNodeById === "function") {
        target.removeDraftNodeById = AgentDraftModule.removeDraftNodeById;
    }
    if (typeof AgentDraftModule.upsertDraftNode === "function") {
        target.upsertDraftNode = AgentDraftModule.upsertDraftNode;
    }
    if (typeof AgentDraftModule.removeDraftConnections === "function") {
        target.removeDraftConnections = AgentDraftModule.removeDraftConnections;
    }
    if (typeof AgentDraftModule.upsertDraftConnection === "function") {
        target.upsertDraftConnection = AgentDraftModule.upsertDraftConnection;
    }
    if (typeof AgentDraftModule.findDraftAxis === "function") {
        target.findDraftAxis = AgentDraftModule.findDraftAxis;
    }
    if (typeof AgentReviseModule.ensureTimeoutErrorBranch === "function") {
        target.ensureTimeoutErrorBranch = AgentReviseModule.ensureTimeoutErrorBranch;
    }
    if (typeof AgentReviseModule.applyOffBranchToDraft === "function") {
        target.applyOffBranchToDraft = AgentReviseModule.applyOffBranchToDraft;
    }
    if (typeof AgentReviseModule.applyAfterMotionPlanToDraft === "function") {
        target.applyAfterMotionPlanToDraft = AgentReviseModule.applyAfterMotionPlanToDraft;
    }
    if (typeof AgentReviseModule.applyPlanAnswerToDraft === "function") {
        target.applyPlanAnswerToDraft = (draft, questionId, rawValue) => AgentReviseModule.applyPlanAnswerToDraft(draft, questionId, rawValue, {
            extractErrorCodeFromText: dependencies.extractErrorCodeFromText
        });
    }
    if (typeof AgentReviseModule.applyAnswersToDraft === "function") {
        target.applyAnswersToDraft = (draft, answers = {}) => AgentReviseModule.applyAnswersToDraft(draft, answers, {
            extractErrorCodeFromText: dependencies.extractErrorCodeFromText
        });
    }

    if (typeof AgentEditorApplyModule.asFiniteNumber === "function") {
        target.asFiniteNumber = AgentEditorApplyModule.asFiniteNumber;
    }
    if (typeof AgentEditorApplyModule.findDraftMotion === "function") {
        target.findDraftMotion = AgentEditorApplyModule.findDraftMotion;
    }
    if (typeof AgentEditorApplyModule.prepareFlowDraftApplyData === "function") {
        target.prepareFlowDraftApplyData = (draft, userMessage = "") => AgentEditorApplyModule.prepareFlowDraftApplyData(draft, userMessage, {
            buildLocalFlowDraft: dependencies.buildLocalFlowDraft,
            genId: dependencies.genId
        });
    }
    if (typeof AgentEditorApplyModule.buildDrawflowNodeSpecs === "function") {
        target.buildDrawflowNodeSpecs = (nodesDraft, axesDraft) => AgentEditorApplyModule.buildDrawflowNodeSpecs(nodesDraft, axesDraft, {
            genId: dependencies.genId,
            findDraftMotion: dependencies.findDraftMotion
        });
    }

    if (typeof AgentFlowExtractModule.buildFlowDraftFromCurrentEditor === "function") {
        target.buildFlowDraftFromCurrentEditor = (drawflowExport, project) =>
            AgentFlowExtractModule.buildFlowDraftFromCurrentEditor(drawflowExport, project, {
                getNodeData: dependencies.getNodeData,
                getNodeTypeFromData: dependencies.getNodeTypeFromData,
                resolveMotionByNodeId: dependencies.resolveMotionByNodeId
            });
    }
    if (typeof AgentFlowPatchModule.validateFlowPatch === "function") {
        target.validateFlowPatch = AgentFlowPatchModule.validateFlowPatch;
    }
    if (typeof AgentFlowPatchModule.resolveAnchor === "function") {
        target.resolveAnchor = AgentFlowPatchModule.resolveAnchor;
    }
    if (typeof AgentFlowPatchModule.applyFlowPatchToDraft === "function") {
        target.applyFlowPatchToDraft = (sourceDraft, patch) =>
            AgentFlowPatchModule.applyFlowPatchToDraft(sourceDraft, patch, {
                genId: dependencies.genId
            });
    }
}
